/**
 * Taurine review carousel.
 *
 * Scoped to <tr-dog-reviews>; all queries run against the element so multiple
 * instances and Theme Editor section reloads stay independent.
 *
 * Scrolling itself is native CSS scroll-snap — this only drives the arrows and
 * keeps their disabled state in sync. No slider library, no clones.
 */
class TrDogReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-trd-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-trd-slide]'));
    this.prevBtn = this.querySelector('[data-trd-prev]');
    this.nextBtn = this.querySelector('[data-trd-next]');

    this.onPrev = () => this.#page(-1);
    this.onNext = () => this.#page(1);
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);

    // Passive: this listener only reads scroll position, so it must never
    // block the compositor during a swipe.
    this.onScroll = () => this.#scheduleSync();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onResize = () => this.#scheduleSync();
    window.addEventListener('resize', this.onResize, { passive: true });

    this.#sync();
  }

  /** How many whole cards fit in the viewport right now. */
  #perView() {
    if (!this.slides.length) return 1;
    const slideWidth = this.slides[0].getBoundingClientRect().width;
    if (!slideWidth) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / slideWidth));
  }

  #step() {
    if (this.slides.length < 2) return this.track.clientWidth;
    // Distance between two slide starts: card width plus the real gap, read
    // from layout rather than assumed from the CSS variable.
    const a = this.slides[0].getBoundingClientRect().left;
    const b = this.slides[1].getBoundingClientRect().left;
    return Math.abs(b - a) || this.track.clientWidth;
  }

  #page(direction) {
    const amount = this.#step() * this.#perView() * direction;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: amount, behavior: reduce ? 'auto' : 'smooth' });
  }

  #scheduleSync() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.#sync();
    });
  }

  #sync() {
    if (!this.prevBtn && !this.nextBtn) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // 2px of slack: sub-pixel layout means scrollLeft rarely hits the exact
    // maximum, which would otherwise leave the next arrow enabled at the end.
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('tr-dog-reviews')) customElements.define('tr-dog-reviews', TrDogReviews);
